import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useScrolled } from "../effects";
import { MagneticButton } from "../helpers";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "Services", id: "services" },
  { label: "Why Us", id: "why" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

const NAV_OFFSET = 72;

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
  window.scrollTo({ top, behavior: "smooth" });
};

export default function Navbar() {
  const scrolled = useScrolled(40);
  const [open, setOpen] = useState(false);

  const handleMobileNav = (id) => {
    setOpen(false);
    // wait for the menu collapse so the scroll target/offset is stable
    setTimeout(() => scrollTo(id), 320);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("home")}
          className="flex items-center gap-3"
        >
          <img src="/paramarsh-mark.webp" alt="Paramarsh Tech" className="h-11 w-11 object-cover rounded-xl border border-brand-orange/40 shadow-[0_0_18px_rgba(255,138,0,0.35)]" />
          <img src="/paramarsh-wordmark.png" alt="Paramarsh Tech" className="h-10 sm:h-11 w-auto object-contain hidden sm:block" />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="group relative font-body text-sm py-1"
            >
              <span className="relative block h-5 overflow-hidden">
                <span className="block leading-5 text-brand-sec transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-full">
                  {l.label}
                </span>
                <span className="block leading-5 text-transparent bg-clip-text bg-gradient-to-r from-[#FF5F00] via-[#FF9D00] to-[#FFC400] transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-full">
                  {l.label}
                </span>
              </span>
              <span
                className="absolute -bottom-0.5 left-0 h-[2px] w-0 rounded-full group-hover:w-full transition-[width] duration-300 ease-out"
                style={{ background: "linear-gradient(to right,#FF5F00,#FFC400)", boxShadow: "0 0 10px rgba(255,138,0,0.8)" }}
              />
            </button>
          ))}
        </nav>

        <MagneticButton
          data-testid="nav-get-started"
          onClick={() => scrollTo("contact")}
          className="hidden md:inline-flex items-center rounded-full px-6 py-2.5 text-sm font-medium text-black"
          style={{ background: "linear-gradient(to right,#FF5F00,#FF9D00,#FFC400)" }}
        >
          Book a Consultant
        </MagneticButton>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#0B0B0E]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => handleMobileNav(l.id)}
                  className="text-left font-body text-brand-sec hover:text-brand-orange transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-get-started"
                onClick={() => handleMobileNav("contact")}
                className="rounded-full px-6 py-3 text-sm font-medium text-black text-center"
                style={{ background: "linear-gradient(to right,#FF5F00,#FF9D00,#FFC400)" }}
              >
                Book a Consultant
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
